import axios from 'axios';
import { useCallback, useEffect, useState } from 'react';
import { defaultPageInfo } from '../../../interface/CommonInterface';
import { IMedication } from '../../../interface/PatientInterface';
import { formatDateTime } from '../../../utils/dateTimeUtils';
import { PatientTabs } from '../../../utils/mappings';
import { addToast } from '../../../utils/toastNotifications';
import { endpoints } from '../../../utils/URL';
import NoDataView from '../../common/NoData';
import Pagination from '../../common/Pagination';
import Loader from '../../common/Loader';

const MedicationScreen = ({ patientId, tabKey }: { patientId: number; tabKey: string }) => {
  const [pageInfo, setPageInfo] = useState(defaultPageInfo);
  const [medications, setMedications] = useState<IMedication[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const getMedications = useCallback(
    async (pageIndex: number) => {
      if (patientId === 0) {
        return;
      }

      const url = `${endpoints.patientDrugs.getByPatient}/${patientId}?pageIndex=${pageIndex}&pageSize=10`;
      setIsLoading(true);
      try {
        const response = await axios.get(url);
        setMedications(response.data.data);

        const responsePageInfo = {
          pageIndex: response.data.pageIndex,
          pageSize: response.data.pageSize,
          pageCount: response.data.pageCount,
        };
        //set page info
        setPageInfo(responsePageInfo);
      } catch (error) {
        addToast('An error occured while getting medications', 'error');
      } finally {
        setIsLoading(false);
      }
    },
    [patientId]
  );

  const redispenseMedication = (medicationId: number) => {
    const response = window.confirm('Are you sure you want to redispense this medication?');
    if (!response) {
      return;
    }
    addToast('Medication successfully redispensed', 'success');
  };

  useEffect(() => {
    if (tabKey !== PatientTabs.medications) {
      return;
    }
    getMedications(1);
  }, [patientId, tabKey, getMedications]);

  const dataView = (
    <div>
      <div className='table-responsive'>
        <table className='table table-hover table-bordered'>
          <thead>
            <tr>
              <th>Drug Name</th>
              <th>Dose</th>
              <th>Prescribed days</th>
              <th>Medication type</th>
              <th>Disease tags</th>
              <th>Start date</th>
              <th>Actions </th>
            </tr>
          </thead>
          <tbody>
            {medications.map((medication) => (
              <tr key={medication.drugId}>
                <td>{medication.drugName}</td>
                <td>{medication.dose}</td>
                <td>{medication.duration}</td>
                <td>{medication.chronic ? 'Chronic' : 'Acute'}</td>
                <td>{medication.diseaseTag}</td>
                <td>{formatDateTime(medication.startDate)}</td>
                <td>
                  <button
                    className='btn text-info'
                    onClick={() => redispenseMedication(medication.drugId)}
                  >
                    <span className='bi bi-recycle custom-single-icon' title='Re-dispense'></span>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pagination
        currentPageNumber={pageInfo.pageIndex}
        numberOfPages={pageInfo.pageCount}
        callback={getMedications}
      />
    </div>
  );

  return (
    <section className='mt-5'>
      <h5 className='mb-5'>Medications</h5>
      {isLoading && <Loader />}
      {!isLoading && medications.length > 0 && dataView}
      {!isLoading && medications.length === 0 && <NoDataView />}
    </section>
  );
};

export default MedicationScreen;
